import React, { useState, useRef, useEffect } from 'react';

const CategorySelector = ({ label, categories, initialSelected = [], onSelectionChange }) => {
  const [selectedItems, setSelectedItems] = useState(initialSelected);
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const dropdownRef = useRef(null);
  
  // Handle item selection
  const toggleItem = (item) => {
    let newSelectedItems;
    
    if (selectedItems.includes(item)) {
      newSelectedItems = selectedItems.filter(i => i !== item);
    } else {
      newSelectedItems = [...selectedItems, item];
    }
    
    setSelectedItems(newSelectedItems);
    
    if (onSelectionChange) {
      onSelectionChange(newSelectedItems);
    }
  };
  
  // Remove selected item
  const removeItem = (item, e) => {
    e.stopPropagation();
    toggleItem(item);
  };

  // Select all filtered items
  const selectAll = () => {
    const newSelectedItems = [...new Set([...selectedItems, ...filteredCategories])];
    setSelectedItems(newSelectedItems);
    
    if (onSelectionChange) {
      onSelectionChange(newSelectedItems);
    }
  };

  // Filter categories based on search
  const filteredCategories = categories.filter(
    cat => cat.toLowerCase().includes(searchText.toLowerCase())
  );

  // Check if all filtered items are selected
  const allFilteredSelected = filteredCategories.length > 0 && 
    filteredCategories.every(cat => selectedItems.includes(cat));

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="relative">
      <label className="block mb-2 text-sm font-medium text-gray-700">
        {label}
      </label>
      
      <div 
        className="flex flex-wrap gap-2 mb-2 min-h-8 p-1"
        onClick={() => setIsOpen(true)}
      >
        {selectedItems.length > 0 ? (
          selectedItems.map((item) => (
            <div 
              key={item}
              className="flex items-center bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm"
            >
              {item}
              <button
                type="button"
                className="ml-2 text-indigo-600 hover:text-indigo-800"
                onClick={(e) => removeItem(item, e)}
              >
                ✕
              </button>
            </div>
          ))
        ) : (
          <div className="text-gray-400 text-sm">Select {label}</div>
        )}
      </div>
      
      <div 
        className="relative"
        ref={dropdownRef}
      >
        <div
          className="w-full p-2.5 bg-gray-50 border border-gray-300 rounded-lg text-sm cursor-pointer flex justify-between items-center"
          onClick={() => setIsOpen(!isOpen)}
        >
          <span className="text-gray-500">Select {label}</span>
          <svg className="w-4 h-4 ml-2" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </div>
        
        {isOpen && (
          <div className="absolute z-[60] w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
            <div className="p-2">
              <input
                type="text"
                className="w-full p-2 border border-gray-300 rounded-md text-sm"
                placeholder="Search..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                autoFocus
              />
            </div>
            
            {/* Select All Button */}
            {filteredCategories.length > 0 && (
              <div className="px-2 pb-2">
                <button
                  type="button"
                  className="w-full px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md border border-gray-300 flex items-center justify-center"
                  onClick={(e) => {
                    e.stopPropagation();
                    selectAll();
                  }}
                >
                  <input
                    type="checkbox"
                    className="mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    checked={allFilteredSelected}
                    onChange={() => {}}
                    onClick={(e) => e.stopPropagation()}
                  />
                  Select All {filteredCategories.length > 1 ? `(${filteredCategories.length} items)` : ''}
                </button>
              </div>
            )}
            
            <ul className="max-h-60 overflow-y-auto py-1">
              {filteredCategories.length > 0 ? (
                filteredCategories.map((category) => (
                  <li 
                    key={category}
                    className={`px-4 py-2 text-sm cursor-pointer hover:bg-gray-100 ${
                      selectedItems.includes(category) ? 'bg-indigo-50' : ''
                    }`}
                    onClick={() => toggleItem(category)}
                  >
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        className="mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        checked={selectedItems.includes(category)}
                        onChange={() => {}}
                        onClick={(e) => e.stopPropagation()}
                      />
                      {category}
                    </div>
                  </li>
                ))
              ) : (
                <li className="px-4 py-2 text-sm text-gray-500">No results found</li>
              )}
            </ul>
            
            <div className="flex items-center justify-between p-2 border-t border-gray-200">
              <span className="text-sm text-gray-500">
                {selectedItems.length} selected
              </span>
              <div>
                <button
                  type="button"
                  className="px-3 py-1 text-sm text-gray-700 hover:bg-gray-100 rounded-md mr-2"
                  onClick={() => {
                    setSelectedItems([]);
                    if (onSelectionChange) {
                      onSelectionChange([]);
                    }
                  }}
                >
                  Clear
                </button>
                <button
                  type="button"
                  className="px-3 py-1 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                  onClick={() => setIsOpen(false)}
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CategorySelector;